import Icon from './Icon'
import { Popover } from 'react-tiny-popover'
import { useState, useRef, useEffect } from 'react'
import { LocalStorageKey } from '../helpers/constants'
import Avatar from '../components/Avatar'
import { LoginResponseDto } from '@ivy-monorepo/shared'
import { authService } from '../services/auth.service'
import { API_URL } from '../config'

interface AccountProfileProps {
  authData: LoginResponseDto | null
  handleLogout: () => void
  theme: string
  onThemeChange: () => void
  isMobile?: boolean
}

const AccountProfile = ({
  authData,
  handleLogout,
  theme,
  onThemeChange,
  isMobile,
}: AccountProfileProps) => {
  const [isProfilePopoverOpen, setIsProfilePopoverOpen] = useState(false)
  const [isLanguagePopoverOpen, setIsLanguagePopoverOpen] = useState(false)
  const [isProfilePopoverVisible, setIsProfilePopoverVisible] = useState(false)
  const [isLanguagePopoverVisible, setIsLanguagePopoverVisible] = useState(false)
  const avatarUploadRef = useRef<HTMLInputElement>(null)
  const [language, setLanguage] = useState('en-US')

  useEffect(() => {
    const currentTheme = localStorage.getItem(LocalStorageKey.theme) ?? 'light'
    const currentLanguage = localStorage.getItem(LocalStorageKey.language) ?? 'en-US'
    setLanguage(currentLanguage)
    document.documentElement.setAttribute('data-theme', currentTheme)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.popover-container') && !target.closest('.account-profile-button')) {
        setIsProfilePopoverOpen(false)
        setIsLanguagePopoverOpen(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isProfilePopoverOpen) {
      setIsProfilePopoverVisible(true)
    } else {
      const timer = setTimeout(() => setIsProfilePopoverVisible(false), 300)
      return () => clearTimeout(timer)
    }
  }, [isProfilePopoverOpen])

  useEffect(() => {
    if (isLanguagePopoverOpen) {
      setIsLanguagePopoverVisible(true)
    } else {
      const timer = setTimeout(() => setIsLanguagePopoverVisible(false), 300)
      return () => clearTimeout(timer)
    }
  }, [isLanguagePopoverOpen])

  const languages = [
    { code: 'en-US', name: 'English' },
    { code: 'es-MX', name: 'Español' },
  ]

  const changeLanguage = (language: string) => {
    localStorage.setItem(LocalStorageKey.language, language)
    window.location.reload()
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate file
    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      alert('Only JPEG, JPG, and PNG files are allowed')
      return
    }

    // Validate size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB')
      return
    }

    try {
      if (!authData) return
      const updatedAccount = await authService.updateAccount({}, file)
      const currentAuthData = JSON.parse(localStorage.getItem(LocalStorageKey.auth) || '{}')

      currentAuthData.account.imageUrl = updatedAccount.imageUrl
      localStorage.setItem(LocalStorageKey.auth, JSON.stringify(currentAuthData))

      // Force a DOM update for the avatar images
      const avatarElements = document.querySelectorAll('.avatar .image')
      avatarElements.forEach(el => {
        const element = el as HTMLElement
        if (element.style.backgroundImage.includes(authData.account.publicId)) {
          // Add a cache-busting parameter
          element.style.backgroundImage = `url(${API_URL}${updatedAccount.imageUrl}?v=${Date.now()})`
        }
      })
    } catch (error) {
      console.error('Failed to upload avatar:', error)
    }
  }

  const toggleIsLanguagePopoverOpen = () => {
    setIsLanguagePopoverOpen(!isLanguagePopoverOpen)
  }

  const toggleIsProfilePopoverOpen = () => {
    if (!isProfilePopoverOpen) {
      setIsProfilePopoverOpen(true)
    }
  }

  const showLog = () => {
    // TODO: Implement log display
  }

  return (
    <div className={`account-profile ${isMobile ? 'mobile' : ''}`}>
      <Popover
        parentElement={document.getElementsByClassName('account-profile')[0] as HTMLElement}
        containerClassName={`popover-container language ${isLanguagePopoverOpen ? 'visible' : ''}`}
        isOpen={isLanguagePopoverVisible}
        content={
          <div className="popover-content language">
            {languages.map(language => (
              <div key={language.code} onClick={() => changeLanguage(language.code)}>
                <img src={`/images/flags/${language.code}.png`} alt={language.name} />
                <span>{language.name}</span>
              </div>
            ))}
          </div>
        }
        positions={isMobile ? ['bottom'] : ['left', 'right']}
      >
        <img
          className="language-chooser"
          src={`/images/flags/${language}.png`}
          alt="Language"
          onClick={e => {
            e.stopPropagation()
            toggleIsLanguagePopoverOpen()
          }}
        />
      </Popover>
      <Popover
        parentElement={document.getElementsByClassName('account-profile')[0] as HTMLElement}
        containerClassName={`popover-container profile ${isProfilePopoverOpen ? 'visible' : ''}`}
        isOpen={isProfilePopoverVisible}
        content={
          <div className="popover-content">
            <Avatar
              className="account-profile-inner"
              url={authData?.account.imageUrl}
              firstName={authData?.account.firstName}
              lastName={authData?.account.lastName}
              onClick={e => {
                e.stopPropagation()
                avatarUploadRef.current?.click()
              }}
            />
            <input
              type="file"
              accept=".jpg,.png"
              ref={avatarUploadRef}
              onChange={handleAvatarChange}
              value={''}
              style={{ visibility: 'hidden', width: '0' }}
            />
            <span className="name">{authData?.account.email}</span>
            <span className="role">
              <span className={`role-name property-tag ${authData?.account.role}`}>
                {authData?.account.role}
              </span>
            </span>
            <hr></hr>
            <div className="item">
              <Icon name="User" />
              {`Profile`}
            </div>
            <div
              className="item"
              onClick={() => {
                onThemeChange()
                setIsProfilePopoverOpen(false)
              }}
            >
              <Icon name={theme === 'light' ? 'Moon' : 'Sun'} />
              {`${theme === 'light' ? 'Dark' : 'Light'} mode`}
            </div>
            <div className="item">
              <Icon name="Gear" />
              {`Settings`}
            </div>
            <div className="item" onClick={showLog}>
              <Icon name="BookOpen" />
              {`Session log`}
            </div>
            <div className="item" onClick={handleLogout}>
              <Icon name="SignOut" />
              {`Log out`}
            </div>
          </div>
        }
        positions={isMobile ? ['bottom'] : ['left', 'right']}
      >
        <Avatar
          className="account-profile-button"
          firstName={authData?.account.firstName}
          lastName={authData?.account.lastName}
          url={authData?.account.imageUrl}
          onClick={toggleIsProfilePopoverOpen}
        />
      </Popover>
    </div>
  )
}

export default AccountProfile
